/**
 * Resume Studio — Style Options panel (V1.4.4).
 *
 * Sits on the preview screen and lets the user tweak:
 *  - Colour palette (5 preset chips)
 *  - 1-page compact toggle
 *  - Elite cover-page split (elite templates only)
 *  - Declaration + Signature (optional footer, common for Indian gov jobs)
 *  - Skill grouping (elite tier — cluster skills into 3 buckets)
 *
 * Persists to backend via PATCH /session/{sid}/style. Preview PDF is
 * re-fetched by the parent on every session update.
 */
import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { Palette, StackSimple, FileText, ListChecks } from "@phosphor-icons/react";
import { API } from "@/lib/api";

const COLOR_LABELS = {
    cobalt: "Cobalt", purple: "Purple", emerald: "Emerald",
    rose: "Rose", slate: "Slate",
};

export default function StyleOptionsPanel({ session, onSessionChange, colorPresets }) {
    const templateId = session?.selected_template || "";
    const isElite = templateId.startsWith("elite_");
    const styleOpts = session?.style_options || { color: "cobalt", compact: false, cover_page: false };
    const fresherInput = session?.fresher_input || {};

    const [color, setColor] = useState(styleOpts.color || "cobalt");
    const [compact, setCompact] = useState(!!styleOpts.compact);
    const [coverPage, setCoverPage] = useState(!!styleOpts.cover_page);
    const [decl, setDecl] = useState(fresherInput.declaration || "");
    const [sig, setSig] = useState(fresherInput.signature_name || "");
    const [placeDate, setPlaceDate] = useState(fresherInput.declaration_place_date || "");
    const [skillGroupsText, setSkillGroupsText] = useState(() => {
        const g = fresherInput.skill_groups;
        if (!g || typeof g !== "object") return "";
        return Object.entries(g)
            .map(([k, v]) => `${k}: ${(v || []).join(", ")}`)
            .join("\n");
    });
    const [busy, setBusy] = useState(false);

    // Sync in on session changes (revision, template swap, etc.)
    useEffect(() => {
        setColor(styleOpts.color || "cobalt");
        setCompact(!!styleOpts.compact);
        setCoverPage(!!styleOpts.cover_page);
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [session?.session_id, session?.selected_template]);

    const parseSkillGroups = () => {
        if (!skillGroupsText.trim()) return {};
        const out = {};
        for (const line of skillGroupsText.split("\n")) {
            const m = line.match(/^\s*([^:]+):\s*(.+)$/);
            if (!m) continue;
            const label = m[1].trim();
            const items = m[2].split(",").map((s) => s.trim()).filter(Boolean);
            if (label && items.length) out[label] = items;
        }
        return out;
    };

    const save = async () => {
        setBusy(true);
        try {
            const body = {
                color, compact, cover_page: isElite ? coverPage : false,
                declaration: decl || null,
                signature_name: sig || null,
                declaration_place_date: placeDate || null,
                skill_groups: isElite ? parseSkillGroups() : null,
            };
            const r = await fetch(
                `${API}/resume-studio/session/${session.session_id}/style`,
                {
                    method: "PATCH",
                    headers: { "Content-Type": "application/json" },
                    body: JSON.stringify(body),
                },
            );
            if (!r.ok) throw new Error((await r.json()).detail || "Style save failed");
            const d = await r.json();
            onSessionChange && onSessionChange(d.session);
            toast.success("Style updated — preview refreshed");
        } catch (err) {
            toast.error(err.message || "Could not save style");
        } finally {
            setBusy(false);
        }
    };

    const presets = colorPresets && colorPresets.length
        ? colorPresets
        : [
            { id: "cobalt", accent: "#0b5bd6" },
            { id: "purple", accent: "#7c3aed" },
            { id: "emerald", accent: "#059669" },
            { id: "rose", accent: "#e11d48" },
            { id: "slate", accent: "#334155" },
        ];

    return (
        <div
            data-testid="style-options-panel"
            className="mt-6 rounded-2xl border border-slate-200 bg-white p-5"
        >
            <div className="flex items-center gap-2 mb-4">
                <Palette weight="fill" size={18} className="text-cobalt-600" />
                <div>
                    <div className="text-sm font-bold">Style options</div>
                    <div className="text-[11px] text-slate-500">
                        Tweak colour, layout, and optional footer. Preview updates instantly.
                    </div>
                </div>
            </div>

            {/* Colour palette */}
            <div className="mb-4">
                <div className="text-[11px] font-semibold text-slate-600 uppercase tracking-wider mb-1.5">
                    Accent colour
                </div>
                <div className="flex flex-wrap gap-2">
                    {presets.map((p) => (
                        <button
                            key={p.id}
                            data-testid={`style-color-${p.id}`}
                            type="button"
                            onClick={() => setColor(p.id)}
                            title={COLOR_LABELS[p.id] || p.id}
                            className={`w-9 h-9 rounded-full transition ring-offset-2 ${
                                color === p.id
                                    ? "ring-2 ring-slate-900"
                                    : "ring-1 ring-slate-200 hover:ring-slate-400"
                            }`}
                            style={{ backgroundColor: p.accent }}
                            aria-label={COLOR_LABELS[p.id] || p.id}
                        />
                    ))}
                </div>
            </div>

            {/* Layout toggles */}
            <div className="mb-4 space-y-2">
                <label className="flex items-center gap-2 cursor-pointer">
                    <input
                        type="checkbox"
                        data-testid="style-compact-toggle"
                        checked={compact}
                        onChange={(e) => setCompact(e.target.checked)}
                        className="rounded"
                    />
                    <StackSimple weight="bold" size={14} className="text-slate-500" />
                    <span className="text-sm text-slate-700">
                        <b>Compact mode</b>
                        <span className="text-[11px] text-slate-500 ml-1">— force onto one page</span>
                    </span>
                </label>
                {isElite && (
                    <label className="flex items-center gap-2 cursor-pointer">
                        <input
                            type="checkbox"
                            data-testid="style-coverpage-toggle"
                            checked={coverPage}
                            onChange={(e) => setCoverPage(e.target.checked)}
                            className="rounded"
                        />
                        <FileText weight="bold" size={14} className="text-slate-500" />
                        <span className="text-sm text-slate-700">
                            <b>Cover page split</b>
                            <span className="text-[11px] text-slate-500 ml-1">— name + summary on page 1</span>
                        </span>
                    </label>
                )}
            </div>

            {/* Declaration + signature (optional) */}
            <details data-testid="style-declaration-section" className="mb-3 group">
                <summary className="cursor-pointer text-xs font-semibold text-slate-600 uppercase tracking-wider py-1.5 hover:text-slate-900">
                    Declaration + Signature <span className="font-normal normal-case text-slate-400">(optional — for gov / PSU jobs)</span>
                </summary>
                <div className="mt-2 space-y-2">
                    <textarea
                        data-testid="style-declaration-input"
                        value={decl}
                        onChange={(e) => setDecl(e.target.value)}
                        placeholder='e.g. "I hereby declare that the information above is true to the best of my knowledge."'
                        rows={2}
                        className="w-full text-xs border border-slate-200 rounded-lg px-2 py-1.5"
                    />
                    <div className="grid grid-cols-2 gap-2">
                        <input
                            data-testid="style-signature-input"
                            value={sig}
                            onChange={(e) => setSig(e.target.value)}
                            placeholder="Signature name"
                            className="w-full text-xs border border-slate-200 rounded-lg px-2 py-1.5"
                        />
                        <input
                            data-testid="style-placedate-input"
                            value={placeDate}
                            onChange={(e) => setPlaceDate(e.target.value)}
                            placeholder="City, Date"
                            className="w-full text-xs border border-slate-200 rounded-lg px-2 py-1.5"
                        />
                    </div>
                </div>
            </details>

            {/* Skill grouping — Elite only */}
            {isElite && (
                <details data-testid="style-skill-groups-section" className="mb-3 group">
                    <summary className="cursor-pointer text-xs font-semibold text-slate-600 uppercase tracking-wider py-1.5 hover:text-slate-900">
                        <ListChecks weight="bold" size={12} className="inline mr-1" />
                        Skill grouping <span className="font-normal normal-case text-slate-400">(elite only)</span>
                    </summary>
                    <div className="mt-2">
                        <textarea
                            data-testid="style-skill-groups-input"
                            value={skillGroupsText}
                            onChange={(e) => setSkillGroupsText(e.target.value)}
                            placeholder={"Programming: Python, Django, Node.js\nTools: Docker, AWS, PostgreSQL\nSoft: Leadership, Communication"}
                            rows={4}
                            className="w-full text-xs font-mono border border-slate-200 rounded-lg px-2 py-1.5"
                        />
                        <div className="text-[10px] text-slate-500 mt-1">
                            One group per line. Format: <code>Label: item1, item2, item3</code>
                        </div>
                    </div>
                </details>
            )}

            <button
                onClick={save}
                disabled={busy}
                data-testid="style-save-btn"
                className="mt-2 w-full bg-cobalt-600 hover:bg-cobalt-700 disabled:opacity-50 text-white font-semibold text-sm py-2 rounded-lg"
            >
                {busy ? "Applying…" : "Apply style"}
            </button>
        </div>
    );
}
